import { Controller } from "@hotwired/stimulus"

// The rendered list's own bookkeeping: the three things about a row leaving or coming back that
// only the browser can know, because the server rebuilds the list AFTER the write and by then the
// thread's place in it is gone.
//
// 1. A row that is leaving collapses instead of blinking out of existence.
// 2. Where it was is remembered, so undoing the write can put it back there.
// 3. If it was the open thread, the pane moves to the row beside it — and the empty copy appears
//    when the last row goes.
const ROW_SELECTOR = ".conversation-row"
const ROW_LINK_SELECTOR = ".conversation-row__link"
const EXIT_DURATION = 180

export default class extends Controller {
  static targets = ["rows", "empty"]

  connect() {
    // Keyed by row id. Reset on connect, which is exactly right: the frame has just re-rendered
    // the list, so every position this held describes a list that is no longer on screen.
    this.removedPositions = new Map()
  }

  // turbo:before-stream-render, for the row stream in a state action's response (see
  // conversations/list_change). Every other stream in that response — the toast, the pane — is
  // left alone.
  interceptRowStream(event) {
    const stream = event.detail.newStream

    switch (stream.getAttribute("action")) {
      case "remove": this.interceptRemoval(event); break
      case "replace": this.interceptReturn(event); break
    }
  }

  interceptRemoval(event) {
    const row = document.getElementById(event.detail.newStream.getAttribute("target"))
    if (!row?.matches(ROW_SELECTOR)) return

    const wasOpenThread = !!row.querySelector(`${ROW_LINK_SELECTOR}[aria-current="true"]`)
    this.removedPositions.set(row.id, [ ...this.rowsTarget.children ].indexOf(row))
    // Collapsing holds this row in the document while the response's other streams render, so for
    // that moment the list contains a row that is already gone. Marked rather than left to be
    // inferred, so nothing hands it something it cannot keep — a rescued keyboard focus, in
    // particular (see focus_rescue_controller).
    row.dataset.leaving = ""

    this.wrapRender(event, async (render) => {
      // Asked after the collapse and before the render: the row is still in the document, so it
      // still has neighbours to ask about, and by now every other row leaving in this same response
      // has marked itself — which is what a bulk change needs and one archive never did.
      const neighbour = this.neighbourOf(row)
      await this.collapse(row)
      await render()
      this.toggleEmptyState()
      if (wasOpenThread) this.advanceTo(neighbour)
    })
  }

  // The nearest row that is actually staying, below first and then above. Skipping the ones on their
  // way out is what a bulk archive needs: fifty removals arrive in one response, and advancing into a
  // row that is also leaving would open a thread only to watch it vanish.
  neighbourOf(row) {
    return this.standingRowFrom(row, "nextElementSibling") ??
           this.standingRowFrom(row, "previousElementSibling")
  }

  standingRowFrom(row, direction) {
    let candidate = row[direction]

    while (candidate && !this.isStandingRow(candidate)) candidate = candidate[direction]

    return candidate
  }

  isStandingRow(element) {
    return element.matches(ROW_SELECTOR) && !("leaving" in element.dataset)
  }

  // A thread coming back onto the list — an undo, mostly. It answers with a replace, and Turbo
  // drops a stream whose target it cannot find, so a row this list has already animated away would
  // return on the server and never on the screen. An empty <li> under the right id is enough to
  // give the swap something to land on, and it goes back at the position remembered above.
  interceptReturn(event) {
    const target = event.detail.newStream.getAttribute("target")
    if (document.getElementById(target)) return

    const returning = event.detail.newStream.querySelector("template")?.content.firstElementChild
    if (!returning?.matches(ROW_SELECTOR)) return

    this.wrapRender(event, async (render) => {
      this.rowsTarget.insertBefore(this.placeholderFor(target),
                                  this.rowsTarget.children[this.removedPositions.get(target)] ?? null)
      await render()
      this.removedPositions.delete(target)
      this.toggleEmptyState()
    })
  }

  // Height and opacity rather than a class, because what the height animates FROM is whatever this
  // row happens to be: one carrying three request types is taller than one carrying none.
  collapse(row) {
    if (this.prefersReducedMotion) return Promise.resolve()

    row.style.overflow = "hidden"
    const animation = row.animate(
      [ { height: `${row.offsetHeight}px`, opacity: 1 }, { height: "0px", opacity: 0 } ],
      { duration: EXIT_DURATION, easing: "ease-in" }
    )

    // Resolved either way. A refresh broadcast landing mid-collapse cancels the animation, and a
    // rejection here would abandon the removal it was only meant to decorate.
    return animation.finished.catch(() => {})
  }

  // Performed as a click on the neighbouring row's own link rather than by rebuilding the pane, so
  // advancing goes through exactly what opening a thread already goes through: the frame
  // navigation, the ?open= thread_url_controller records, and the row that lights up.
  advanceTo(neighbour) {
    const link = neighbour?.querySelector(ROW_LINK_SELECTOR)
    if (link) {
      link.click()
      return
    }

    this.forgetOpenThread()
  }

  // Nothing to advance to, so the response has already emptied the pane. The address bar has to
  // stop naming a thread that is no longer open, or a refresh would reopen what was just archived.
  forgetOpenThread() {
    const url = new URL(window.location)
    if (!url.searchParams.has("open")) return

    url.searchParams.delete("open")
    history.replaceState(history.state, "", url)
  }

  // The copy is rendered with the list and merely hidden while there are rows (see
  // conversations/_list), so revealing it is a toggle rather than a fetch — the server has no way
  // to write it here without rebuilding the frame the row is animating inside.
  toggleEmptyState() {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.hidden = this.rowsTarget.children.length > 0
  }

  placeholderFor(id) {
    const placeholder = document.createElement("li")
    placeholder.id = id
    return placeholder
  }

  wrapRender(event, wrapped) {
    const render = event.detail.render
    event.detail.render = (stream) => wrapped(() => render(stream))
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
